import axios from "axios";

/** Set to true to show console logs; false to hide them app-wide. */
export const DisplayConsoleLogs = false;

if (!DisplayConsoleLogs) {
  const noop = () => {};
  console.log = noop;
  console.info = noop;
  console.debug = noop;
  console.warn = noop;
  console.error = noop;
}

const API_BASE_URL =
  import.meta.env.VITE_API_BASE_URL ||
  (import.meta.env.DEV ? "/api" : "https://api.salonsyncr.com/api");

/** DRF list endpoints may return an array or `{ count, next, results }`. */
export function unwrapList(payload) {
  if (Array.isArray(payload)) return payload;
  if (payload && Array.isArray(payload.results)) return payload.results;
  return [];
}

export function parsePaginated(payload) {
  if (Array.isArray(payload)) {
    return {
      results: payload,
      count: payload.length,
      totalRevenue: null,
      isServerPaginated: false,
    };
  }
  const results = Array.isArray(payload?.results) ? payload.results : [];
  const rawRevenue = payload?.total_revenue;
  const totalRevenue =
    rawRevenue === undefined || rawRevenue === null || rawRevenue === ""
      ? null
      : Number(rawRevenue);
  return {
    results,
    count: Number.isFinite(payload?.count) ? payload.count : results.length,
    totalRevenue: Number.isFinite(totalRevenue) ? totalRevenue : null,
    isServerPaginated: Number.isFinite(payload?.count) && Array.isArray(payload?.results),
  };
}

function pagedListParams({ page = 1, pageSize = 10, search = "", ...rest } = {}) {
  const params = { page, page_size: pageSize };
  const query = (search || "").trim();
  if (query) params.search = query;
  Object.entries(rest).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      params[key] = value;
    }
  });
  return params;
}

function toRelativeApiPath(url) {
  try {
    const parsed = new URL(url, "https://api.salonsyncr.com");
    let path = `${parsed.pathname}${parsed.search}`;
    if (path.startsWith("/api/")) {
      path = path.slice(4);
    }
    return path;
  } catch {
    return url;
  }
}

async function getAllPages(url, config = {}) {
  const first = await apiClient.get(url, {
    ...config,
    params: { page_size: 100, limit: 100, ...(config.params || {}) },
  });
  const payload = first.data;
  if (Array.isArray(payload)) {
    return first;
  }
  if (!payload || !Array.isArray(payload.results)) {
    return first;
  }

  const all = [...payload.results];
  let next = payload.next;
  let guard = 0;
  while (next && guard < 100) {
    guard += 1;
    let page;
    if (typeof next === "number" || (typeof next === "string" && /^\d+$/.test(String(next)))) {
      page = await apiClient.get(url, {
        ...config,
        params: {
          page_size: 100,
          limit: 100,
          ...(config.params || {}),
          page: next,
        },
      });
    } else {
      page = await apiClient.get(toRelativeApiPath(next));
    }
    const data = page.data;
    if (Array.isArray(data)) {
      all.push(...data);
      break;
    }
    all.push(...(data?.results || []));
    next = data?.next || null;
  }

  return { ...first, data: all };
}

// Create axios instance with default config
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

// Request interceptor to add auth token to requests
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("authToken");
    if (token) {
      config.headers.Authorization = `Token ${token}`;
    }
    if (typeof FormData !== "undefined" && config.data instanceof FormData) {
      if (config.headers && typeof config.headers.delete === "function") {
        config.headers.delete("Content-Type");
      } else if (config.headers) {
        delete config.headers["Content-Type"];
        if (config.headers.common) {
          delete config.headers.common["Content-Type"];
        }
      }
    }
    return config;
  },
  (error) => Promise.reject(error),
);

// Response interceptor to handle common errors
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    // Log detailed error information
    if (error.response) {
      console.log("Error response:", error.response.data);
    }

    // Handle 401 Unauthorized errors (token expired, etc.)
    if (error.response && error.response.status === 401) {
      localStorage.removeItem("authToken");
      window.location.href = "/login";
    }
    return Promise.reject(error);
  },
);

// Auth API endpoints
const authAPI = {
  login: (credentials) => apiClient.post("/login/", credentials),
  logout: () => apiClient.post("/logout/"),
  forgotPassword: (email) => apiClient.post("/forgot-password/", { email }),
  verifyOtp: (email, otp) => apiClient.post("/verify-otp/", { email, otp }),
  resetPassword: (email, newPassword, confirmPassword) =>
    apiClient.post("/reset-password/", {
      email,
      new_password: newPassword,
      confirm_password: confirmPassword,
    }),
};

// User API endpoints
const userAPI = {
  getProfile: () => apiClient.get("/users/profile/"),
  updateProfile: (data) => apiClient.put("/users/profile/", data),
  changePassword: (passwordData) =>
    apiClient.post("/change-password/", passwordData),
};

// Jobs API endpoints
const jobsAPI = {
  getJobs: () => getAllPages("/jobs/"),
  getJobDetails: (id) => apiClient.get(`/jobs/${id}/`),
};

// Content API endpoints
const contentAPI = {
  getBlogs: () => getAllPages("/content/blogs/"),
  getBlogDetails: (id) => apiClient.get(`/content/blogs/${id}/`),
  getTrendingBlogs: (params = {}) =>
    apiClient.get("/blogs/trending/", { params: pagedListParams(params) }),
  addTrendingBlog: (formData) =>
    apiClient.post("/blogs/trending/", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    }),
  updateTrendingBlog: (id, formData) => {
    console.log(`Updating trending blog ${id} with form data`);

    // Log the form data for debugging
    for (let pair of formData.entries()) {
      console.log(`${pair[0]}: ${pair[1]}`);
    }

    return apiClient.patch(`/blogs/trending/${id}/`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
  },
  deleteTrendingBlog: (id) => apiClient.delete(`/blogs/trending/${id}/`),
  getConsultations: (params = {}) =>
    apiClient.get("/blogs/consultations/", { params: pagedListParams(params) }),
  addConsultation: (formData) =>
    apiClient.post("/blogs/consultations/", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    }),
  updateConsultation: (id, formData) =>
    apiClient.patch(`/blogs/consultations/${id}/`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    }),
  deleteConsultation: (id) => apiClient.delete(`/blogs/consultations/${id}/`),
};

// Training API endpoints
const trainingAPI = {
  getModules: () => getAllPages("/blogs/training/"),
  getModuleDetails: (id) => apiClient.get(`/blogs/training/${id}/`),
  addModule: (formData) =>
    apiClient.post("/blogs/training/", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    }),
  updateModule: (id, formData) => {
    console.log(`Updating module ${id} with form data`);

    // Log the delete_images value if it exists
    if (formData.get("delete_images")) {
      console.log("Sending delete_images:", formData.get("delete_images"));
    }

    return apiClient.patch(`/blogs/training/${id}/`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
  },
  toggleModuleActive: (id) =>
    apiClient.patch(`/blogs/training/${id}/toggle_active/`),
  getTrainingRegistrations: () =>
    getAllPages("/blogs/training-registrations/"),
  addTrainingRegistration: (registrationData) =>
    apiClient.post("/blogs/training-registrations/", registrationData),
};

// Admin API endpoints
const adminAPI = {
  getDashboardOverview: (params = {}) =>
    apiClient.get("/dashboard/overview/", { params }),
  getPendingApprovals: (params = {}) =>
    apiClient.get("/admin-approval/", { params: pagedListParams(params) }),
  approveSalon: (approvalData) =>
    apiClient.post("/admin-approval/", approvalData),
  getSalonOwners: (params = {}) =>
    apiClient.get("/salon-owners/", { params: pagedListParams(params) }),
  updateSalonStatus: (salonId, statusData) =>
    apiClient.patch(`/update-salon-status/${salonId}/`, statusData),
  getAllSalons: () => getAllPages("/salons/"),
  getSuperAdmins: () => getAllPages("/super-admins/"),
  getSpecializations: () => getAllPages("/specializations/"),
  addSpecialization: (data) => apiClient.post("/specializations/", data),
  updateSpecialization: (id, data) =>
    apiClient.patch(`/specializations/${id}/`, data),
  deleteSpecialization: (id) => apiClient.delete(`/specializations/${id}/`),
};

// Job seekers API endpoints
const jobSeekersAPI = {
  getJobSeekers: (params = {}) =>
    apiClient.get("/jobseekers-list/", { params: pagedListParams(params) }),
  getAllJobSeekers: () => getAllPages("/jobseekers-list/"),
  getJobSeekerDetails: (id) => apiClient.get(`/jobseekers-list/${id}/`),
  updateJobSeekerStatus: (id, statusData) =>
    apiClient.patch(`/jobseekers-list/${id}/`, statusData),
};

// Analytics API endpoints
const analyticsAPI = {
  getMembershipPayments: (params = {}) =>
    apiClient.get("/analytics/membership-payments/", {
      params: pagedListParams(params),
    }),
  addMembershipPayment: (paymentData) =>
    apiClient.post("/analytics/membership-payments/", paymentData),
  updateMembershipPayment: (id, paymentData) =>
    apiClient.patch(`/analytics/membership-payments/${id}/`, paymentData),
  deleteMembershipPayment: (id) =>
    apiClient.delete(`/analytics/membership-payments/${id}/`),
};

// Product API endpoints
const productAPI = {
  getProducts: (params = {}) =>
    apiClient.get("/blogs/products/", { params: pagedListParams(params) }),
  getProductDetails: (id) => apiClient.get(`/blogs/products/${id}/`),
  addProduct: (formData) =>
    apiClient.post("/blogs/products/", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
      transformRequest: [
        function (data) {
          // Ensure proper encoding for form data
          if (data instanceof FormData) {
            // FormData will be handled automatically by axios
            return data;
          }
          return data;
        },
      ],
    }),
  updateProduct: (id, formData) => {
    // Try PATCH first, if it fails, try PUT
    return apiClient
      .patch(`/blogs/products/${id}/`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
        transformRequest: [
          function (data) {
            // Ensure proper encoding for form data
            if (data instanceof FormData) {
              // FormData will be handled automatically by axios
              return data;
            }
            return data;
          },
        ],
        timeout: 30000, // 30 seconds timeout
        // Add additional configuration to handle encoding issues
        maxContentLength: Infinity,
        maxBodyLength: Infinity,
      })
      .catch((error) => {
        // If PATCH fails, try PUT as fallback
        return apiClient.put(`/blogs/products/${id}/`, formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
          transformRequest: [
            function (data) {
              if (data instanceof FormData) {
                return data;
              }
              return data;
            },
          ],
          timeout: 30000,
          maxContentLength: Infinity,
          maxBodyLength: Infinity,
        });
      });
  },
  deleteProduct: (id) => apiClient.delete(`/blogs/products/${id}/`),
};

export {
  apiClient,
  authAPI,
  userAPI,
  jobsAPI,
  contentAPI,
  trainingAPI,
  adminAPI,
  jobSeekersAPI,
  analyticsAPI,
  productAPI,
};
